import { useState } from "react";
import { CalendarDays, Download, FileUp, Sparkles, RefreshCw, CheckCircle2, ArrowLeft, Merge, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Modal, inp, lbl } from "../gear/GearBits";

/* Import trips from TripIt & other tools: A) .ics calendar file, B) AI paste/upload. */
const MODE_ICONS = { flight: "✈", train: "🚆", bus: "🚌", ferry: "⛴", car: "🚗", taxi: "🚕", hotel: "🏨", reservation: "🍽", other: "•" };

export default function TripImport({ onClose, onImported }) {
  const [tab, setTab] = useState("ics"); // ics | ai
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // {trips, source}
  const [include, setInclude] = useState([]);

  const showPreview = (data) => {
    setPreview(data);
    setInclude(data.trips.map(() => true));
  };

  const uploadIcs = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/trip-import/ics", fd, { timeout: 60000 });
      showPreview(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not read that file"); }
    setBusy(false);
  };

  const runAi = async (file) => {
    if (!file && !text.trim()) { toast.error("Paste your itinerary or upload a file"); return; }
    setBusy(true);
    const fd = new FormData();
    if (text.trim()) fd.append("text", text.trim());
    if (file) fd.append("file", file);
    try {
      const { data } = await api.post("/trip-import/ai", fd, { timeout: 120000 });
      showPreview(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "AI couldn't read that itinerary"); }
    setBusy(false);
  };

  const confirm = async () => {
    const chosen = preview.trips.filter((_, i) => include[i]);
    if (!chosen.length) { toast.error("Select at least one trip"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/trip-import/confirm", { trips: chosen });
      toast.success(`${data.created.length} trip${data.created.length > 1 ? "s" : ""} imported into My Travel Hub`);
      onImported();
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };

  const setTitle = (i, v) => setPreview((p) => ({ ...p, trips: p.trips.map((t, j) => (j === i ? { ...t, title: v } : t)) }));

  const combineSelected = () => {
    const idxs = include.map((v, i) => (v ? i : -1)).filter((i) => i >= 0);
    if (idxs.length < 2) { toast.error("Tick at least two trips to combine"); return; }
    const chosen = idxs.map((i) => preview.trips[i]);
    const segments = chosen.flatMap((t) => t.segments || [])
      .sort((a, b) => `${a.date || "9999"} ${a.time || "99"}`.localeCompare(`${b.date || "9999"} ${b.time || "99"}`));
    const stops = [...new Set(chosen.flatMap((t) => t.stops || []))].slice(0, 10);
    const dates = chosen.flatMap((t) => [t.start_date, t.end_date]).filter(Boolean).sort();
    const merged = { title: chosen[0].title, start_date: dates[0] || null,
      end_date: dates[dates.length - 1] || null, stops, segments };
    const restTrips = preview.trips.filter((_, i) => !idxs.includes(i));
    const restInclude = include.filter((_, i) => !idxs.includes(i));
    setPreview((p) => ({ ...p, trips: [merged, ...restTrips] }));
    setInclude([true, ...restInclude]);
    toast.success(`Combined ${chosen.length} trips into one — ${segments.length} legs`);
  };

  return (
    <Modal title="Import trips" onClose={onClose}>
      <div data-testid="trip-import-modal">
        {!preview ? (
          <>
            <div className="flex gap-1.5 mb-4">
              <button onClick={() => setTab("ics")} data-testid="import-tab-ics"
                className={`px-3 py-2 text-sm font-700 border ${tab === "ics" ? "border-orange-500 bg-orange-50 text-orange-800" : "border-zinc-300"}`}>
                <CalendarDays className="w-4 h-4 inline mr-1" /> Calendar file (.ics)
              </button>
              <button onClick={() => setTab("ai")} data-testid="import-tab-ai"
                className={`px-3 py-2 text-sm font-700 border ${tab === "ai" ? "border-violet-500 bg-violet-50 text-violet-800" : "border-zinc-300"}`}>
                <Sparkles className="w-4 h-4 inline mr-1" /> AI import (paste / upload)
              </button>
            </div>

            {tab === "ics" && (
              <div data-testid="import-ics-panel">
                <p className="text-sm text-zinc-700 mb-3">
                  Import your travel history from <b>TripIt, Google Calendar, Kayak</b> or any tool that
                  exports a calendar (.ics) file. We'll detect trips, flights and hotels automatically.
                </p>
                <details className="text-xs text-zinc-600 border border-zinc-200 bg-zinc-50 p-3 mb-4">
                  <summary className="font-700 cursor-pointer">Where do I get my TripIt file?</summary>
                  <ol className="list-decimal ml-4 mt-2 space-y-1">
                    <li>Sign in at tripit.com → click your name → <b>Settings</b></li>
                    <li>Open <b>Calendar Feed</b> and copy the feed link (webcal://…)</li>
                    <li>Paste the link in a new browser tab, changing <b>webcal://</b> to <b>https://</b> — a .ics file downloads</li>
                    <li>Upload that file below</li>
                  </ol>
                  <p className="mt-2 text-amber-800"><b>Heads-up:</b> TripIt's feed usually contains
                    <b> upcoming trips only</b> — for past trips, use the <b>AI import</b> tab with pasted
                    text, a PDF or screenshots of each trip.</p>
                </details>
                <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-orange-600 text-white text-sm font-700 cursor-pointer hover:bg-orange-500">
                  <FileUp className="w-4 h-4" /> {busy ? "Reading…" : "Upload .ics file"}
                  <input type="file" accept=".ics,text/calendar" className="hidden" onChange={uploadIcs}
                    disabled={busy} data-testid="import-ics-file" />
                </label>
              </div>
            )}

            {tab === "ai" && (
              <div data-testid="import-ai-panel">
                <p className="text-sm text-zinc-700 mb-3">
                  Paste itinerary text (confirmation emails, a TripIt trip page, your notes) — or upload a
                  <b> PDF / screenshot</b> — and our AI extracts the trips for you.
                </p>
                <label className={lbl}>Itinerary text</label>
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7} maxLength={20000}
                  className={inp} placeholder={"e.g.\nUA 512 · SFO → ORD · Sep 1, 9:00 AM\nHilton Chicago · Sep 1–4\nUA 88 · ORD → SFO · Sep 4, 5:00 PM"}
                  data-testid="import-ai-text" />
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <button onClick={() => runAi(null)} disabled={busy} data-testid="import-ai-run"
                    className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-violet-700 text-white text-sm font-700 hover:bg-violet-600 disabled:opacity-50">
                    {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {busy ? "Extracting…" : "Extract trips"}
                  </button>
                  <label className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-violet-300 text-violet-700 text-sm font-700 cursor-pointer hover:bg-violet-50">
                    <FileUp className="w-4 h-4" /> or upload PDF / image
                    <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden"
                      disabled={busy} data-testid="import-ai-file"
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) runAi(f); }} />
                  </label>
                </div>
              </div>
            )}
          </>
        ) : (
          <div data-testid="import-preview">
            <p className="text-sm text-zinc-700 mb-3">
              Found <b>{preview.trips.length} trip{preview.trips.length > 1 ? "s" : ""}</b> — review, rename
              and untick anything you don't want, then import.
              {preview.trips.length > 1 && <> One journey split into several trips? Tick them and use <b>Combine into one trip</b>.</>}
            </p>
            <div className="space-y-3 max-h-[46vh] overflow-y-auto pr-1">
              {preview.trips.map((t, i) => (
                <div key={i} className={`border p-3 ${include[i] ? "border-emerald-300 bg-emerald-50/40" : "border-zinc-200 opacity-60"}`}
                  data-testid={`import-trip-${i}`}>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={include[i]} data-testid={`import-trip-check-${i}`}
                      onChange={() => setInclude((arr) => arr.map((v, j) => (j === i ? !v : v)))} />
                    <input value={t.title} onChange={(e) => setTitle(i, e.target.value)} maxLength={80}
                      className="flex-1 border border-zinc-300 px-2 py-1.5 text-sm font-700 bg-white" data-testid={`import-trip-title-${i}`} />
                    <span className="text-xs font-mono text-muted-foreground shrink-0">
                      {t.start_date || "?"}{t.end_date && t.end_date !== t.start_date ? ` → ${t.end_date}` : ""}
                    </span>
                  </div>
                  <ul className="mt-2 space-y-0.5 text-xs text-zinc-700 ml-6">
                    {t.segments.slice(0, 6).map((s, k) => (
                      <li key={k}>{MODE_ICONS[s.mode] || "•"} {s.mode}{s.ref ? ` ${s.ref}` : ""}
                        {s.from_place ? ` · ${s.from_place}` : ""}{s.to_place ? ` → ${s.to_place}` : ""}{s.date ? ` · ${s.date}` : ""}</li>
                    ))}
                    {t.segments.length > 6 && <li className="text-muted-foreground">…and {t.segments.length - 6} more</li>}
                  </ul>
                  {t.overlaps && (
                    <div className="mt-2 ml-6 flex items-start gap-1.5 border border-amber-300 bg-amber-50 text-amber-800 px-2 py-1.5 text-xs"
                      data-testid={`import-overlap-${i}`}>
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>Overlaps your existing trip <b>“{t.overlaps.title}”</b> ({t.overlaps.start_date}{t.overlaps.end_date ? ` → ${t.overlaps.end_date}` : ""}) —
                        you may already have this one. Untick it to skip.</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 mt-4">
              <button onClick={() => setPreview(null)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-zinc-800" data-testid="import-back">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              {include.filter(Boolean).length >= 2 && (
                <button onClick={combineSelected} data-testid="import-combine"
                  className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-sky-500 text-sky-700 text-sm font-700 hover:bg-sky-50">
                  <Merge className="w-4 h-4" /> Combine into one trip
                </button>
              )}
              <button onClick={confirm} disabled={busy || !include.some(Boolean)} data-testid="import-confirm"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-emerald-600 text-white text-sm font-700 hover:bg-emerald-700 disabled:opacity-50">
                {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Import {include.filter(Boolean).length} trip{include.filter(Boolean).length !== 1 ? "s" : ""}
              </button>
            </div>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-4 flex items-center gap-1">
          <Download className="w-3 h-3" /> Everything imports as private trips — you can edit, share or delete them any time.
        </p>
      </div>
    </Modal>
  );
}
